import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { cargoService, Cargo, CreateCargoDTO } from '../services/cargoService';
import { permissaoService, Permissao } from '../services/permissaoService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useForm, Controller } from 'react-hook-form';

const GerenciarCargosPage: React.FC = () => {
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<Permissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [cargoAtual, setCargoAtual] = useState<Cargo | null>(null);

  const {
    control,
    register,
    handleSubmit: handleSubmitForm,
    reset,
    formState: { errors },
  } = useForm<CreateCargoDTO>({
    defaultValues: {
      nome: '',
      descricao: '',
      permissaoIds: [],
    },
  });

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [cargosData, permissoesData] = await Promise.all([
        cargoService.listarCargos(),
        permissaoService.listarPermissoes(),
      ]);
      setCargos(cargosData);
      setPermissoesDisponiveis(permissoesData);
    } catch (error) {
      toast.error('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const handleOpenDialog = (cargo?: Cargo) => {
    if (cargo) {
      setCargoAtual(cargo);
      // @ts-ignore - O backend retorna as permissões no cargo, mas o tipo Cargo não tem.
      const permissoesDoCargo = cargo.permissoes?.map(p => p.id) || [];
      reset({
        nome: cargo.nome.toUpperCase(),
        descricao: cargo.descricao?.toUpperCase() || '',
        permissaoIds: permissoesDoCargo,
      });
    } else {
      setCargoAtual(null);
      reset({
        nome: '',
        descricao: '',
        permissaoIds: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const onSubmit = async (formData: CreateCargoDTO) => {
    try {
      if (cargoAtual) {
        await cargoService.atualizarCargo(cargoAtual.id, formData);
        toast.success('Cargo atualizado com sucesso!');
      } else {
        await cargoService.criarCargo(formData);
        toast.success('Cargo criado com sucesso!');
      }
      handleCloseDialog();
      carregarDados();
    } catch (error) {
      toast.error('Erro ao salvar cargo.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este cargo?')) {
      try {
        await cargoService.excluirCargo(id);
        toast.success('Cargo excluído com sucesso!');
        carregarDados();
      } catch (error) {
        toast.error('Erro ao excluir cargo.');
      }
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            <VpnKeyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
            Gerenciamento de Cargos
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Novo Cargo
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargos.map((cargo) => (
                  <TableRow key={cargo.id} hover>
                    <TableCell>{cargo.nome.toUpperCase()}</TableCell>
                    <TableCell>{cargo.descricao?.toUpperCase()}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton color="primary" onClick={() => handleOpenDialog(cargo)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton color="error" onClick={() => handleDelete(cargo.id)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth component="form" onSubmit={handleSubmitForm(onSubmit)}>
        <DialogTitle>{cargoAtual ? 'Editar Cargo' : 'Novo Cargo'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nome do Cargo"
                fullWidth
                {...register('nome', { required: 'Nome é obrigatório' })}
                error={!!errors.nome}
                helperText={errors.nome?.message}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Descrição" fullWidth multiline rows={2} {...register('descricao')}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="permissaoIds"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={permissoesDisponiveis}
                    getOptionLabel={(option) => `${option.entidade.toUpperCase()}: ${option.descricao}`}
                    value={permissoesDisponiveis.filter(p => field.value.includes(p.id))}
                    onChange={(_, newValue) => field.onChange(newValue.map(p => p.id))}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) => value.map((option, index) => (
                      <Chip variant="outlined" label={`${option.entidade}: ${option.acao}`} {...getTagProps({ index })} />
                    ))}
                    renderInput={(params) => (
                      <TextField {...params} label="Permissões" placeholder="Selecione as permissões" />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </MainLayout>
  );
};

export default GerenciarCargosPage;